/**
 * @file ATICreateWrapper.tsx
 *
 * Wrapper around ATIDialog to handle submitting and feeding in initial data, specifically for creating a request
 */
import React from "react";

import { CreateRequestReduxActionTypes } from "pages/new/context/reducer/types";
import { useCreateRequestContext } from "pages/new/context/useCreateRequestContext";
import { AdditionalTripInfoState } from "../form/schemaValidation";
import { TemporaryPassenger } from "types";
import ATIDialog from "./ATIDialog";
import first from "lodash/first";
import { formatPhoneNumber } from "utils/phoneNumberFormatter/phoneNumberFormatter";

type ATICreateWrapperProps = {
  onClose: () => void;
  open: boolean;
  trip: {
    id: string;
    totalGroupSize: number;
    note?: string;
    temporaryPassenger?: TemporaryPassenger;
    routes: {
      id: string;
      carryOnLuggage?: number;
      checkedLuggage?: number;
      oversizeLuggage?: number;
    }[];
  };
};

function ATICreateWrapper(props: ATICreateWrapperProps) {
  const { onClose, open, trip } = props;

  // hooks
  const [createRequestState, dispatch] = useCreateRequestContext();

  // derived state — find the paired leg from context
  const pairedTrip = createRequestState.returnTrip
    ? trip.id === createRequestState.trip.id
      ? createRequestState.returnTrip
      : createRequestState.trip
    : undefined;
  // event handlers
  const handleSubmit = (additionalTripInfoState: AdditionalTripInfoState) => {
    const {
      note,
      totalGroupSize,
      temporaryPassenger,
      route,
      selectedPassenger,
      applyTemporaryPassengerToAllLegs,
    } = additionalTripInfoState;

    const { formatted, international } =
      formatPhoneNumber(
        additionalTripInfoState.temporaryPassenger.phoneFields.mobilePhone,
        additionalTripInfoState.temporaryPassenger.phoneFields.phoneCountryCode,
        additionalTripInfoState.temporaryPassenger.phoneFields
          .phoneCountryDialCode
      ) || {};

    const builtTemporaryPassenger = {
      ...temporaryPassenger.phoneFields,
      name: temporaryPassenger.name,
      mobilePhone: formatted,
      mobilePhoneInternational: international,
    };

    dispatch({
      type: CreateRequestReduxActionTypes.UpdateAdditionalTripInfo,
      payload: {
        totalGroupSize,
        note,
        id: trip.id,
        // merge phone fields back into temporaryPassenger to match graphql schema for TemporaryPassenger
        temporaryPassenger: builtTemporaryPassenger,
        routes: [
          {
            id: route.id,
            carryOnLuggage: route.carryOnLuggage,
            checkedLuggage: route.checkedLuggage,
            oversizeLuggage: route.oversizeLuggage,
          },
        ],
        selectedPassenger,
      },
    });

    if (applyTemporaryPassengerToAllLegs && pairedTrip) {
      dispatch({
        type: CreateRequestReduxActionTypes.UpdateAdditionalTripInfo,
        payload: {
          id: pairedTrip.id,
          totalGroupSize: pairedTrip.totalGroupSize,
          note: pairedTrip.note,
          temporaryPassenger: builtTemporaryPassenger,
          routes: pairedTrip.routes,
          selectedPassenger: pairedTrip.selectedPassenger ?? selectedPassenger,
        },
      });
    }

    onClose();
  };

  return (
    <ATIDialog
      onSubmit={handleSubmit}
      onClose={onClose}
      open={open}
      isSubmitting={false}
      isRoundTrip={!!pairedTrip}
      initialState={{
        ...trip,
        route: first(trip.routes),
        selectedPassenger: null,
      }}
    />
  );
}

export default ATICreateWrapper;
